import { sessionStore as sessionStorage } from "./local/device-store";
import { useEffect, useMemo, useState } from "react";
import { AppsInTossBannerSlot } from "../../ait/src/banner";

export type WebAdPlacement = "home-bottom" | "region-bottom";
export type AdEnvironment = Record<string, string | boolean | undefined>;

export type AdSenseConfig = Readonly<{
  active: boolean;
  client: string | null;
  slot: string | null;
}>;

const clientPattern = /^ca-pub-\d+$/u;
const slotPattern = /^\d+$/u;

export function placementForPath(pathname: string): WebAdPlacement | null {
  if (pathname === "/") return "home-bottom";
  if (/^\/r\/[^/]+\/[^/]+\/?$/u.test(pathname)) return "region-bottom";
  return null;
}

export function resolveAdSenseConfig(placement: WebAdPlacement, env: AdEnvironment): AdSenseConfig {
  const enabled = env.VITE_WEB_ADS_ENABLED === "true" || env.VITE_WEB_ADS_ENABLED === true;
  const client = typeof env.VITE_ADSENSE_CLIENT === "string" && clientPattern.test(env.VITE_ADSENSE_CLIENT) ? env.VITE_ADSENSE_CLIENT : null;
  const candidate = placement === "home-bottom" ? env.VITE_ADSENSE_HOME_SLOT : env.VITE_ADSENSE_REGION_SLOT;
  const slot = typeof candidate === "string" && slotPattern.test(candidate) ? candidate : null;
  return { active: enabled && Boolean(client && slot), client, slot };
}

function providerEnvironment(): AdEnvironment {
  return {
    VITE_WEB_ADS_ENABLED: import.meta.env.VITE_WEB_ADS_ENABLED,
    VITE_ADSENSE_CLIENT: import.meta.env.VITE_ADSENSE_CLIENT,
    VITE_ADSENSE_HOME_SLOT: import.meta.env.VITE_ADSENSE_HOME_SLOT,
    VITE_ADSENSE_REGION_SLOT: import.meta.env.VITE_ADSENSE_REGION_SLOT,
  };
}

function loadAdSense(client: string): Promise<void> {
  const id = "arabom-adsense-sdk";
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === "true") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement("script");
    const done = () => { script.dataset.loaded = "true"; resolve(); };
    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", () => reject(new Error("adsense_sdk_failed")), { once: true });
    if (!existing) {
      script.id = id;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
      document.head.appendChild(script);
    }
  });
}

function AdSenseSlot({ placement }: { placement: WebAdPlacement }) {
  const config = useMemo(() => resolveAdSenseConfig(placement, providerEnvironment()), [placement]);
  useEffect(() => {
    if (!config.active || !config.client) return;
    let disposed = false;
    void loadAdSense(config.client).then(() => {
      if (disposed) return;
      const runtime = window as typeof window & { adsbygoogle?: unknown[] };
      runtime.adsbygoogle ??= [];
      runtime.adsbygoogle.push({});
    }).catch(() => undefined);
    return () => { disposed = true; };
  }, [config]);
  if (!config.active || !config.client || !config.slot) return null;
  return <aside className="ad-slot" data-placement={placement} aria-label="광고">
    <ins className="adsbygoogle" style={{ display: "block" }} data-ad-client={config.client} data-ad-slot={config.slot} data-ad-format="auto" data-full-width-responsive="true" />
  </aside>;
}

export function RouteAdSlot() {
  const placement = placementForPath(window.location.pathname);
  const [firstValueReached, setFirstValueReached] = useState(() => sessionStorage.getItem("arabom:ad-first-value:missing") === "1");
  useEffect(() => {
    const mark = () => setFirstValueReached(true);
    addEventListener("arabom:first-value", mark);
    return () => removeEventListener("arabom:first-value", mark);
  }, []);
  if (import.meta.env.VITE_CHANNEL === "apps_in_toss") {
    if (!firstValueReached) return null;
    return <AppsInTossBannerSlot pathname={window.location.pathname}/>;
  }
  if (!placement || !firstValueReached) return null;
  return <AdSenseSlot placement={placement}/>;
}
